import React, { useEffect, useState } from 'react';
import RRHHNativeShell from './RRHHNativeShell';
import { rrhhCoreGet, rrhhCorePost } from './rrhhCoreApi';
import './RRHHNative.css';

const DEFAULT_APPROVAL = { minutes: 10 };
const DEFAULT_ALERTS = { greenMinutes: 15, yellowMinutes: 30 };

function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : fallback;
}

function deviceUrl(token) {
  return `${window.location.origin}/reloj-facial?token=${encodeURIComponent(token)}`;
}

function mapsUrl(place) {
  if (place?.google_maps_url) return place.google_maps_url;
  if (place?.latitud !== null && place?.latitud !== undefined && place?.longitud !== null && place?.longitud !== undefined) {
    return `https://www.google.com/maps?q=${place.latitud},${place.longitud}`;
  }
  return '';
}

function distanceBetween(left, right) {
  const toRadians = (value) => (Number(value) * Math.PI) / 180;
  const lat1 = toRadians(left.latitud);
  const lat2 = toRadians(right.latitud);
  const deltaLat = lat2 - lat1;
  const deltaLon = toRadians(right.longitud) - toRadians(left.longitud);
  const value = Math.sin(deltaLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  const clamped = Math.min(1, Math.max(0, value));
  return 6371000 * 2 * Math.atan2(Math.sqrt(clamped), Math.sqrt(1 - clamped));
}

function DeviceRow({ device, places, onToggle, onDelete, onLocation, onRegenerate, onCopy }) {
  const lastUsed = device.last_used_at ? new Date(device.last_used_at).toLocaleString('es-UY', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false, hourCycle: 'h23',
  }) : 'Sin uso';
  const expires = device.expires_at ? new Date(device.expires_at).toLocaleDateString('es-UY') : 'Sin vencimiento';
  const place = places.find((item) => Number(item.id) === Number(device.location_id));
  const link = device.token ? deviceUrl(device.token) : '';
  return <article className="rrhh-native-card soft rrhh-face-device-row">
    <div className="rrhh-face-device-main">
      <div><strong>{device.nombre}</strong><span className="text-sm text-dim">{device.activo ? 'Activo' : 'Inactivo'} · Último uso: {lastUsed} · {expires}</span></div>
      <label className="rrhh-face-location-field"><span>Lugar físico</span><select className="form-input" value={device.location_id || ''} onChange={(event) => onLocation(device.id, event.target.value)}><option value="">Sin configurar</option>{places.map((item) => <option key={item.id} value={item.id} disabled={item.latitud === null || item.longitud === null}>{item.name}{item.latitud === null || item.longitud === null ? ' · sin coordenadas' : ''}</option>)}</select></label>
      {place && <div className="rrhh-face-place-detail"><span>{place.name}</span><small>{place.latitud}, {place.longitud}</small>{mapsUrl(place) && <a href={mapsUrl(place)} target="_blank" rel="noreferrer">Ver en Google Maps</a>}</div>}
      <div className="rrhh-face-device-link">{link ? <><input className="form-input" readOnly value={link} /><button className="btn btn-secondary" type="button" onClick={() => onCopy(link)}>Copiar enlace</button></> : <><span className="text-sm text-dim">El enlace de este reloj heredado no puede recuperarse.</span><button className="btn btn-secondary" type="button" onClick={() => onRegenerate(device.id)}>Regenerar enlace</button></>}</div>
    </div>
    <div className="rrhh-native-toolbar-group rrhh-face-device-actions">
      {link && <button className="btn btn-secondary" type="button" onClick={() => onRegenerate(device.id)}>Regenerar</button>}
      <button className="btn btn-secondary" type="button" onClick={() => onToggle(device.id)}>{device.activo ? 'Desactivar' : 'Activar'}</button>
      <button className="btn btn-danger" type="button" onClick={() => onDelete(device.id)}>Eliminar</button>
    </div>
  </article>;
}

export default function ConfiguracionPage() {
  const [section, setSection] = useState('parametros');
  const [approval, setApproval] = useState(DEFAULT_APPROVAL);
  const [alerts, setAlerts] = useState(DEFAULT_ALERTS);
  const [devices, setDevices] = useState([]);
  const [places, setPlaces] = useState([]);
  const [deviceName, setDeviceName] = useState('');
  const [deviceLocationId, setDeviceLocationId] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  function message(text) {
    setNotice(text);
    window.setTimeout(() => setNotice(''), 2600);
  }

  async function refresh() {
    setLoading(true); setError('');
    try {
      const [settings, clockDevices, availablePlaces] = await Promise.all([
        rrhhCoreGet('/configuracion'),
        rrhhCoreGet('/relojes-faciales'),
        rrhhCoreGet('/relojes-faciales-lugares'),
      ]);
      const values = Object.fromEntries((settings || []).map((entry) => [entry.clave, entry.valor]));
      setApproval({ ...DEFAULT_APPROVAL, ...(values.approval_tolerance || {}) });
      setAlerts({ ...DEFAULT_ALERTS, ...(values.alert_tolerance || {}) });
      setDevices(Array.isArray(clockDevices) ? clockDevices : []);
      setPlaces(Array.isArray(availablePlaces) ? availablePlaces : []);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la configuración');
    } finally { setLoading(false); }
  }

  useEffect(() => { refresh(); }, []);

  async function saveParameters() {
    setSaving(true); setError('');
    try {
      const cleanApproval = { minutes: numberValue(approval.minutes, DEFAULT_APPROVAL.minutes) };
      const greenMinutes = numberValue(alerts.greenMinutes, DEFAULT_ALERTS.greenMinutes);
      const cleanAlerts = { greenMinutes, yellowMinutes: Math.max(greenMinutes, numberValue(alerts.yellowMinutes, DEFAULT_ALERTS.yellowMinutes)) };
      await Promise.all([
        rrhhCorePost('/configuracion', { clave: 'approval_tolerance', valor: cleanApproval }),
        rrhhCorePost('/configuracion', { clave: 'alert_tolerance', valor: cleanAlerts }),
      ]);
      setApproval(cleanApproval); setAlerts(cleanAlerts); message('Parámetros guardados');
    } catch (err) { setError(err.message || 'No se pudieron guardar los parámetros'); }
    finally { setSaving(false); }
  }

  async function createDevice(event) {
    event.preventDefault();
    if (!deviceName.trim()) return setError('Indicá un nombre para el reloj facial');
    if (!deviceLocationId) return setError('Elegí el lugar físico del reloj facial');
    setSaving(true); setError('');
    try {
      const device = await rrhhCorePost('/relojes-faciales', { nombre: deviceName.trim(), location_id: Number(deviceLocationId) });
      setDeviceName(''); setDeviceLocationId(''); setGeneratedLink(deviceUrl(device.token));
      await refresh(); message('Reloj facial creado');
    } catch (err) { setError(err.message || 'No se pudo crear el reloj facial'); }
    finally { setSaving(false); }
  }

  async function detectClosestPlace() {
    if (!navigator.geolocation) return setError('Este dispositivo no permite obtener la ubicación GPS');
    const geocodedPlaces = places.filter((place) => place.latitud !== null && place.longitud !== null);
    if (!geocodedPlaces.length) return setError('No hay lugares activos con coordenadas configuradas');
    setDetectingLocation(true); setError('');
    try {
      const position = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 0,
      }));
      const current = { latitud: position.coords.latitude, longitud: position.coords.longitude };
      const closest = geocodedPlaces.map((place) => ({ ...place, distance: distanceBetween(current, place) }))
        .sort((left, right) => left.distance - right.distance)[0];
      setDeviceLocationId(String(closest.id));
      message(`Lugar más cercano: ${closest.name} · ${Math.round(closest.distance)} m`);
    } catch {
      setError('No se pudo obtener la ubicación GPS. Revisá el permiso de ubicación del navegador.');
    } finally { setDetectingLocation(false); }
  }

  async function toggleDevice(id) {
    try { await rrhhCorePost(`/relojes-faciales/${id}/toggle`, {}); await refresh(); message('Reloj facial actualizado'); }
    catch (err) { setError(err.message || 'No se pudo actualizar el reloj facial'); }
  }

  async function deleteDevice(id) {
    if (!window.confirm('¿Eliminar este reloj facial? El historial de marcas se conserva.')) return;
    try { await rrhhCorePost(`/relojes-faciales/${id}/delete`, {}); await refresh(); message('Reloj facial eliminado'); }
    catch (err) { setError(err.message || 'No se pudo eliminar el reloj facial'); }
  }

  async function updateDeviceLocation(id, locationId) {
    if (!locationId) return setError('Elegí un lugar físico para el reloj');
    try {
      await rrhhCorePost(`/relojes-faciales/${id}/ubicacion`, { location_id: Number(locationId) });
      await refresh(); message('Lugar físico actualizado');
    } catch (err) { setError(err.message || 'No se pudo actualizar el lugar físico'); }
  }

  async function regenerateDeviceLink(id) {
    if (!window.confirm('El enlace anterior dejará de funcionar. ¿Regenerar el enlace de este reloj?')) return;
    try {
      const device = await rrhhCorePost(`/relojes-faciales/${id}/regenerar-enlace`, {});
      const link = deviceUrl(device.token);
      setGeneratedLink(link);
      await refresh(); message('Enlace regenerado');
    } catch (err) { setError(err.message || 'No se pudo regenerar el enlace'); }
  }

  async function copyDeviceLink(link) {
    try { await navigator.clipboard?.writeText(link); message('Enlace copiado'); }
    catch { setError('No se pudo copiar el enlace'); }
  }

  const actions = <button className="btn btn-primary" type="button" disabled={saving || section !== 'parametros'} onClick={saveParameters}>Guardar cambios</button>;
  return <RRHHNativeShell title="Configuración" subtitle="Parámetros operativos y dispositivos del reloj facial." actions={actions}>
    {error && <div className="alert alert-error">{error}</div>}{notice && <div className="alert alert-success">{notice}</div>}
    <div className="rrhh-native-tabs" role="tablist"><button className={`rrhh-native-tab ${section === 'parametros' ? 'active' : ''}`} type="button" onClick={() => setSection('parametros')}>Parámetros</button><button className={`rrhh-native-tab ${section === 'relojes' ? 'active' : ''}`} type="button" onClick={() => setSection('relojes')}>Relojes faciales</button></div>
    {loading ? <div className="rrhh-native-card rrhh-native-empty">Cargando configuración canónica...</div> : section === 'parametros' ? <section className="rrhh-native-grid rrhh-settings-grid">
      <label className="rrhh-native-card rrhh-setting-field"><small>Pre-aprobación</small><strong>Tolerancia de jornal</strong><span className="text-sm text-dim">Margen permitido para pre-aprobar una jornada.</span><div><input className="form-input" type="number" min="0" value={approval.minutes} onChange={(event) => setApproval({ minutes: event.target.value })} /><span>min</span></div></label>
      <label className="rrhh-native-card rrhh-setting-field"><small>Alertas</small><strong>Umbral amarillo</strong><span className="text-sm text-dim">Desfasaje de atención media.</span><div><input className="form-input" type="number" min="0" value={alerts.greenMinutes} onChange={(event) => setAlerts((current) => ({ ...current, greenMinutes: event.target.value }))} /><span>min</span></div></label>
      <label className="rrhh-native-card rrhh-setting-field"><small>Alertas</small><strong>Umbral rojo</strong><span className="text-sm text-dim">Desfasaje de prioridad alta.</span><div><input className="form-input" type="number" min="0" value={alerts.yellowMinutes} onChange={(event) => setAlerts((current) => ({ ...current, yellowMinutes: event.target.value }))} /><span>min</span></div></label>
    </section> : <section className="rrhh-native-list">
      <section className="rrhh-native-card"><div className="rrhh-payroll-section-head"><div><h2>Nuevo reloj facial</h2><p>El reloj queda asociado a un lugar del master y todas sus marcas heredan esa ubicación.</p></div></div><form className="rrhh-face-device-form" onSubmit={createDevice}><input className="form-input" value={deviceName} onChange={(event) => setDeviceName(event.target.value)} placeholder="Nombre del reloj · Ej: DEPÓSITO" /><select className="form-input" value={deviceLocationId} onChange={(event) => setDeviceLocationId(event.target.value)}><option value="">Lugar físico...</option>{places.map((place) => <option key={place.id} value={place.id} disabled={place.latitud === null || place.longitud === null}>{place.name}{place.latitud === null || place.longitud === null ? ' · sin coordenadas' : ''}</option>)}</select><button className="btn btn-secondary" disabled={detectingLocation} type="button" onClick={detectClosestPlace}>{detectingLocation ? 'Detectando...' : 'Detectar lugar'}</button><button className="btn btn-primary" disabled={saving} type="submit">Generar enlace</button></form>{generatedLink && <div className="rrhh-face-generated-link"><input className="form-input" readOnly value={generatedLink} /><button className="btn btn-secondary" type="button" onClick={() => copyDeviceLink(generatedLink)}>Copiar</button></div>}</section>
      {devices.length ? devices.map((device) => <DeviceRow key={device.id} device={device} places={places} onToggle={toggleDevice} onDelete={deleteDevice} onLocation={updateDeviceLocation} onRegenerate={regenerateDeviceLink} onCopy={copyDeviceLink} />) : <div className="rrhh-native-card rrhh-native-empty">Todavía no hay relojes faciales configurados.</div>}
    </section>}
  </RRHHNativeShell>;
}
